/* src/components/suggestions/suggestionsLogic.js */

// All Firestore access for the Suggestion Box. Shared by the Admin Portal and
// the Tutor Portal — the UI component receives a `viewerRole` and this module
// is told whether the viewer is an admin.
//
// Collection: suggestions/{suggestionId}
//   title          string   (required)
//   description    string   (required, plain text)
//   urgency        'low' | 'medium' | 'high' | 'critical'
//   status         'new' | 'read' | 'completed'
//   createdBy      uid (of the author; matches firestore.rules)
//   createdByName / createdByEmail
//   createdAt / updatedAt        server timestamps
//   editedAt / editedByName      stamped when a suggestion is edited
//   readAt / readBy / readByUid            audit trail for "Mark as read" (admin)
//   completedAt / completedBy / completedByUid   audit trail for "Mark as completed" (admin)
//
// `readByUid` / `completedByUid` exist so the Suggestions nav badge can tell a
// Visibility (enforced in firestore.rules): admins read EVERY suggestion,
// tutors read only the ones they authored. Writes: admins may edit / change
// status / delete any suggestion; a tutor may only EDIT their own (and the
// rules restrict that update to title/description/urgency + audit fields).
// Reads are sorted client-side so no composite index is required (an equality
// filter on createdBy combined with an orderBy on createdAt would need one).

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";

// ==========================================
// CONSTANTS
// ==========================================

// Order matters: this is the canonical low → high scale.
export const URGENCY_LEVELS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

export const URGENCY_RANK = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

export const DEFAULT_URGENCY = "medium";

export const STATUSES = [
  { value: "new", label: "New" },
  { value: "read", label: "Read" },
  { value: "completed", label: "Completed" },
];

export function urgencyMeta(value) {
  const key = String(value || "").trim().toLowerCase();
  return URGENCY_LEVELS.find((u) => u.value === key) || URGENCY_LEVELS[1];
}

export function statusMeta(value) {
  const key = String(value || "").trim().toLowerCase();
  return STATUSES.find((s) => s.value === key) || STATUSES[0];
}

// ==========================================
// INTERNAL HELPERS
// ==========================================

function toMillis(ts) {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  return null;
}

// Reads the signed-in user's profile from users/{uid} so submissions are
// stamped with a human-readable author (mirrors the announcements convention).
async function getCurrentProfile() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in.");

  const snap = await getDoc(doc(db, "users", user.uid));
  const data = snap.exists() ? snap.data() || {} : {};

  return {
    uid: user.uid,
    email: user.email || data.email || "",
    name: data.name || user.displayName || "User",
    role: data.role || "unknown",
  };
}

// ==========================================
// READS
// ==========================================

/**
 * Subscribe to the suggestions the viewer is allowed to see.
 * Admins get every suggestion; tutors get only their own.
 * Returns the Firestore unsubscribe function.
 */
export function subscribeSuggestions({ uid, isAdmin }, onRows, onError) {
  if (!uid) return () => {};

  const base = collection(db, "suggestions");

  // A tutor's query MUST filter by createdBy — the security rules only permit
  // reading their own docs, and Firestore rejects queries it cannot prove are
  // safe. Admins read the whole collection.
  const q = isAdmin ? query(base) : query(base, where("createdBy", "==", uid));

  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => {
        const data = d.data() || {};
        return {
          id: d.id,
          ...data,
          createdAtMs: toMillis(data.createdAt),
          updatedAtMs: toMillis(data.updatedAt),
          editedAtMs: toMillis(data.editedAt),
          // Status audit trail as ms — the Suggestions nav badge needs to know
          // WHEN a suggestion was read / completed (see suggestionsNotifications).
          readAtMs: toMillis(data.readAt),
          completedAtMs: toMillis(data.completedAt),
        };
      });
      onRows(rows);
    },
    (err) => {
      console.error("suggestions listener error:", err);
      if (typeof onError === "function") onError(err);
    }
  );
}

// ==========================================
// WRITES
// ==========================================

/** Create a suggestion on behalf of the signed-in user (admin OR tutor). */
export async function createSuggestion({ title, description, urgency }) {
  const author = await getCurrentProfile();

  const cleanTitle = (title || "").trim();
  const cleanDescription = (description || "").trim();
  const cleanUrgency = URGENCY_RANK[urgency] ? urgency : DEFAULT_URGENCY;

  if (!cleanTitle) throw new Error("Title is required.");
  if (!cleanDescription) throw new Error("Description is required.");

  const docData = {
    title: cleanTitle,
    description: cleanDescription,
    urgency: cleanUrgency,
    status: "new",
    createdBy: author.uid,
    createdByName: author.name,
    createdByEmail: author.email,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    readAt: null,
    readBy: null,
    readByUid: null,
    completedAt: null,
    completedBy: null,
    completedByUid: null,
  };

  const ref = await addDoc(collection(db, "suggestions"), docData);
  return ref.id;
}

/**
 * Edit a suggestion's content. Allowed for admins on ANY suggestion and for a
 * tutor on their OWN suggestion — firestore.rules also restrict a tutor update
 * to exactly the fields written here.
 *
 * Status is deliberately NOT touched: editing does not silently mark a
 * suggestion read/unread.
 */
export async function updateSuggestion(suggestionId, { title, description, urgency }) {
  if (!suggestionId) throw new Error("Missing suggestion id.");

  const editor = await getCurrentProfile();

  const cleanTitle = (title || "").trim();
  const cleanDescription = (description || "").trim();
  const cleanUrgency = URGENCY_RANK[urgency] ? urgency : DEFAULT_URGENCY;

  if (!cleanTitle) throw new Error("Title is required.");
  if (!cleanDescription) throw new Error("Description is required.");

  await updateDoc(doc(db, "suggestions", suggestionId), {
    title: cleanTitle,
    description: cleanDescription,
    urgency: cleanUrgency,
    editedAt: serverTimestamp(),
    editedByName: editor.name,
    updatedAt: serverTimestamp(),
  });
}

// ── Admin-only status transitions ──────────────────────────────────────────
// Each state has a reverse transition so an accidental misclick can be undone.

export async function markSuggestionRead(suggestionId) {
  if (!suggestionId) throw new Error("Missing suggestion id.");
  const admin = await getCurrentProfile();
  await updateDoc(doc(db, "suggestions", suggestionId), {
    status: "read",
    readAt: serverTimestamp(),
    readBy: admin.name,
    // Uid alongside the display name: the Suggestions nav badge uses it to tell
    // "someone else did this" from "I did this" (names are not unique).
    readByUid: admin.uid,
    updatedAt: serverTimestamp(),
  });
}

/** Undo "Mark as read" — clears the read audit trail again. */
export async function markSuggestionUnread(suggestionId) {
  if (!suggestionId) throw new Error("Missing suggestion id.");
  await updateDoc(doc(db, "suggestions", suggestionId), {
    status: "new",
    readAt: null,
    readBy: null,
    readByUid: null,
    updatedAt: serverTimestamp(),
  });
}

export async function markSuggestionCompleted(suggestionId) {
  if (!suggestionId) throw new Error("Missing suggestion id.");
  const admin = await getCurrentProfile();
  await updateDoc(doc(db, "suggestions", suggestionId), {
    status: "completed",
    completedAt: serverTimestamp(),
    completedBy: admin.name,
    completedByUid: admin.uid,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Undo "Mark as completed". Instead of guessing, restore the state implied by
 * the surviving audit trail: back to "read" if it had been read before being
 * completed, otherwise back to "new".
 */
export async function markSuggestionIncomplete(suggestionId, wasRead) {
  if (!suggestionId) throw new Error("Missing suggestion id.");
  await updateDoc(doc(db, "suggestions", suggestionId), {
    status: wasRead ? "read" : "new",
    completedAt: null,
    completedBy: null,
    completedByUid: null,
    updatedAt: serverTimestamp(),
  });
}

/** Admin-only delete. */
export async function deleteSuggestion(suggestionId) {
  if (!suggestionId) throw new Error("Missing suggestion id.");
  await deleteDoc(doc(db, "suggestions", suggestionId));
}
